import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { uid } from "../utils/helpers";
import { buildClusters, mergeClusters, parsePastedRows } from "../utils/csv";
import { dlFile } from "../utils/export";
import { Icons } from "../components/Icons";

export default function SemanticsView() {
  const { proj, activeId, getStore, setStore, dataRef, projects, setProjects, apiFetch,
    saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks,
    flash, setView, view, dark, setDark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    userRole, currentUser, loadProjectData, openP, infoId, setInfoId, setActiveId } = useApp();
  const rnStrip = (u) => String(u || "").replace(/https?:\/\/[^/]+/, "") || "/";
  const [rn, setRn] = useState(null);
  const openRename = (url) => setRn({ oldUrl: url, newUrl: url, mode: "single", busy: false, preview: null, republish: true });
  const rnPreview = async () => {
    setRn(r => ({ ...r, busy: true }));
    try { const res = await apiFetch(`/api/projects/${activeId}/rename-url`, { method: "POST", body: JSON.stringify({ oldUrl: rn.oldUrl, newUrl: rn.newUrl, mode: rn.mode, dryRun: true }) }); const d = await res.json(); setRn(r => ({ ...r, busy: false, preview: d.report || d })); }
    catch (e) { setRn(r => ({ ...r, busy: false })); flash("Помилка: " + e.message); }
  };
  const rnApply = async () => {
    setRn(r => ({ ...r, busy: true }));
    try {
      const res = await apiFetch(`/api/projects/${activeId}/rename-url`, { method: "POST", body: JSON.stringify({ oldUrl: rn.oldUrl, newUrl: rn.newUrl, mode: rn.mode, republish: rn.republish }) });
      const d = await res.json(); if (!res.ok) throw new Error(d.error || "err");
      if (rn.republish) { try { await apiFetch(`/api/projects/${activeId}/seo-texts/republish`, { method: "POST" }); } catch {} }
      flash("URL змінено каскадно ✓" + (rn.republish ? " (переопубліковано)" : "")); setRn(null); if (loadProjectData) loadProjectData(activeId);
    } catch (e) { setRn(r => ({ ...r, busy: false })); flash("Помилка: " + e.message); }
  };
  const store = proj ? getStore(activeId) : { clusters: [] };
  const clusters = store.clusters || [];
  const [sortCol, setSortCol] = useState("frequency");
  const [sortDir, setSortDir] = useState("desc");
  const [filterUrl, setFilterUrl] = useState("");
  const [tick, setTick] = useState(0);
  const [showMissing, setShowMissing] = useState(false);
  const [missingTab, setMissingTab] = useState("commercial"); // commercial | blog
  const [gscRows, setGscRows] = useState(null); // null = not loaded
  const [serpstatRows, setSerpstatRows] = useState(null); // null = not loaded
  const [semTab, setSemTab] = useState("main"); // main | blog
  const [checkedMissing, setCheckedMissing] = useState(new Set());
  const [showAssign, setShowAssign] = useState(false);
  const [unmatchedKws, setUnmatchedKws] = useState([]);
  const [assignUrl, setAssignUrl] = useState({});
  const [importing, setImporting] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefData, setBriefData] = useState(null);
  const [briefProgress, setBriefProgress] = useState(null);
  const [kwSearch, setKwSearch] = useState("");
  const [selectedKws, setSelectedKws] = useState(new Set());
  const [tagInput, setTagInput] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteErr, setPasteErr] = useState("");
  const token = localStorage.getItem("metago_token");

  // Load GSC data for this project
  useEffect(() => {
    if (!activeId || !token) return;
    fetch(`/api/gsc/saved/${activeId}`, { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.found && d.rows) setGscRows(d.rows); })
      .catch(() => {});
  }, [activeId, token]);

  // Load Serpstat domain keywords for this project
  useEffect(() => {
    if (!activeId || !token) return;
    fetch(`/api/projects/${activeId}/serpstat/domain-keywords`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.keywords && d.keywords.length) setSerpstatRows(d.keywords); })
      .catch(() => {});
  }, [activeId, token]);

  // Question/info query detection
  const isInfoQuery = (q) => {
    const lower = q.toLowerCase().trim();
    if (lower.endsWith("?")) return true;
    const infoWords = ["как ", "что ", "почему ", "зачем ", "где ", "когда ", "можно ли ", "чем ", "какой ", "какая ", "какие ", "каким ", "сколько ", "чи ", "як ", "що ", "чому ", "де ", "коли ", "скільки ", "який ", "яка ", "які ", "what ", "how ", "why ", "where ", "when ", "which ", "who ", "is ", "are ", "can ", "does ", "do "];
    return infoWords.some(w => lower.startsWith(w));
  };

  // Build flat keyword list with cluster info
  const allKeywords = useMemo(() => {
    const result = [];
    clusters.forEach(c => {
      (c.keywords || []).forEach(k => {
        result.push({
          keyword: k.keyword,
          frequency: k.frequency,
          url: c.url,
          clusterName: c.hfKeyword || "",
          hasMeta: !!c.meta,
          hasContent: !!c.content,
          h1: c.meta?.h1 || "",
          httpCode: c.httpCode,
          clusterId: c.id,
          isInfo: isInfoQuery(k.keyword),
          isExpansion: c.status === "EXPANSION",
          tags: k.tags || "",
        });
      });
    });
    return result;
  }, [clusters, tick]);

  // Missing keywords from GSC + Serpstat
  const missingKeywords = useMemo(() => {
    const existingSet = new Set(allKeywords.map(k => k.keyword.toLowerCase().trim()));
    const seen = new Set();
    const result = [];
    // GSC source
    if (gscRows && gscRows.length) {
      gscRows.forEach(r => {
        if (!r || !r.query || r.impressions <= 1) return;
        const key = r.query.toLowerCase().trim();
        if (existingSet.has(key) || seen.has(key)) return;
        seen.add(key);
        result.push({ ...r, page: r.page || "", isInfo: isInfoQuery(r.query), source: "gsc" });
      });
    }
    // Serpstat source
    if (serpstatRows && serpstatRows.length) {
      serpstatRows.forEach(r => {
        if (!r || !r.keyword || r.in_clusters) return;
        const key = r.keyword.toLowerCase().trim();
        if (existingSet.has(key) || seen.has(key)) return;
        seen.add(key);
        result.push({ query: r.keyword, impressions: r.frequency || 0, clicks: 0, position: r.position || 0, page: r.url || "", isInfo: isInfoQuery(r.keyword), source: "serpstat" });
      });
    }
    return result;
  }, [gscRows, serpstatRows, allKeywords]);

  const urls = useMemo(() => [...new Set(allKeywords.map(k => k.url))], [allKeywords]);

  const filtered = useMemo(() => {
    let data = semTab === "blog" ? allKeywords.filter(k => k.isInfo) : allKeywords.filter(k => !k.isInfo);
    if (filterUrl) data = data.filter(k => k.url && k.url.toLowerCase().includes(filterUrl.toLowerCase()));
    if (kwSearch) {
      const q = kwSearch.toLowerCase();
      data = data.filter(k => k.keyword.toLowerCase().includes(q));
    }
    return [...data].sort((a, b) => {
      const av = a[sortCol], bv = b[sortCol];
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
  }, [allKeywords, sortCol, sortDir, filterUrl, semTab, kwSearch]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
    setPage(0);
  };

  const checkAllUrls = async () => {
    for (let i = 0; i < clusters.length; i++) {
      const c = clusters[i];
      if (!c.url || c.httpCode != null) continue;
      const s = getStore(activeId);
      const updated = [...(s.clusters || [])];
      updated[i] = { ...updated[i], httpCode: "..." };
      setStore(activeId, { ...s, clusters: updated });
      setTick(v => v + 1);
      try {
        const resp = await fetch("/api/check-url", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
          body: JSON.stringify({ url: c.url }),
        });
        const data = await resp.json();
        const s2 = getStore(activeId);
        const u2 = [...(s2.clusters || [])];
        u2[i] = { ...u2[i], httpCode: data.status };
        setStore(activeId, { ...s2, clusters: u2 });
        setTick(v => v + 1);
      } catch {
        const s2 = getStore(activeId);
        const u2 = [...(s2.clusters || [])];
        u2[i] = { ...u2[i], httpCode: "err" };
        setStore(activeId, { ...s2, clusters: u2 });
        setTick(v => v + 1);
      }
    }
  };

  const codeBg = (code) => {
    if (code === "...") return { background: "#fef3c7", color: "#92400e" };
    if (code === 200) return { background: "#d1fae5", color: "#065f46" };
    if (typeof code === "number" && code >= 300 && code < 400) return { background: "#dbeafe", color: "#1e40af" };
    if (typeof code === "number" && code >= 400) return { background: "#fee2e2", color: "#991b1b" };
    if (code === "error" || code === "err" || code === "timeout") return { background: "#fee2e2", color: "#991b1b" };
    return { background: dark ? "#252535" : "#f3f4f6", color: dark ? "#8b8ba0" : "#666" };
  };

  const stripDomain = useCallback((url) => {
    try { const u = new URL(url); return u.pathname + (u.search || ""); } catch { return url; }
  }, []);

  // Pagination for main table — reset on filter changes
  const [page, _setPage] = useState(0);
  const setPage = _setPage;
  useEffect(() => { _setPage(0); }, [filterUrl, semTab]);
  const PAGE_SIZE = 200;
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pagedFiltered = useMemo(() => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [filtered, page]);

  const thStyle = { padding: "8px 10px", textAlign: "left", fontSize: 12, fontWeight: 600, color: t.textMuted, cursor: "pointer", userSelect: "none", borderBottom: `2px solid ${t.border}` };
  const tdStyle = { padding: "6px 10px", fontSize: 13, borderBottom: `1px solid ${t.border}` };
  const sortIcon = (col) => sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  const [missingPage, setMissingPage] = useState(0);
  const [missingSource, setMissingSource] = useState("all"); // all | gsc | serpstat
  const [missingSortCol, setMissingSortCol] = useState("impressions"); // query | source | page | impressions | clicks | position | matched
  const [missingSortDir, setMissingSortDir] = useState("desc"); // asc | desc
  const MISSING_PAGE_SIZE = 200;
  const toggleMissingSort = (col) => {
    if (missingSortCol === col) {
      setMissingSortDir(d => d === "desc" ? "asc" : "desc");
    } else {
      setMissingSortCol(col);
      setMissingSortDir("desc");
    }
    setMissingPage(0);
  };
  const missingSortIcon = (col) => missingSortCol === col ? (missingSortDir === "asc" ? " ↑" : " ↓") : "";

  // Safe path extraction (handles both full URLs and relative paths)
  const getPath = (url) => {
    if (!url) return "";
    try { return new URL(url).pathname.replace(/\/$/, ""); } catch { return url.split("?")[0].replace(/\/$/, ""); }
  };

  // Pre-built URL index for fast matching
  const urlIndex = useMemo(() => {
    const exactMap = new Map();
    const partsList = [];
    clusters.forEach(c => {
      if (!c.url) return;
      const p = getPath(c.url);
      exactMap.set(p, c.url);
      partsList.push({ parts: p.split("/").filter(Boolean), url: c.url });
    });
    return { exactMap, partsList };
  }, [clusters]);

  const matchUrl = useCallback((gscPageUrl) => {
    if (!gscPageUrl) return null;
    const gscPath = getPath(gscPageUrl);
    if (!gscPath) return null;
    const exact = urlIndex.exactMap.get(gscPath);
    if (exact) return exact;
    const gscParts = gscPath.split("/").filter(Boolean);
    let bestMatch = null, bestScore = 0;
    for (const { parts, url } of urlIndex.partsList) {
      let score = 0;
      const len = Math.min(gscParts.length, parts.length);
      for (let i = 0; i < len; i++) {
        if (gscParts[i] === parts[i]) score++; else break;
      }
      if (score > bestScore && score >= 2) { bestScore = score; bestMatch = url; }
    }
    return bestMatch;
  }, [urlIndex]);

  const mainKw = useMemo(() => allKeywords.filter(k => !k.isInfo), [allKeywords]);
  const blogKw = useMemo(() => allKeywords.filter(k => k.isInfo), [allKeywords]);
  const totalKw = allKeywords.length;
  const totalFreq = useMemo(() => allKeywords.reduce((s, k) => s + k.frequency, 0), [allKeywords]);
  const metaPages = clusters.filter(c => c.meta).length;
  const contentPages = clusters.filter(c => c.content).length;
  const missingCommercial = useMemo(() => missingKeywords.filter(k => !k.isInfo), [missingKeywords]);
  const missingBlog = useMemo(() => missingKeywords.filter(k => k.isInfo), [missingKeywords]);

  // Pre-compute URL matches for all missing keywords (once, via useMemo)
  const missingMatches = useMemo(() => {
    const map = new Map();
    const pageCache = new Map();
    missingKeywords.forEach(r => {
      if (!pageCache.has(r.page)) pageCache.set(r.page, matchUrl(r.page));
      map.set(r.query, pageCache.get(r.page));
    });
    return map;
  }, [missingKeywords, matchUrl]);

  // Export усіх упущених ключів у Excel (BOM + ;-CSV, як utils/export)
  const exportMissingCSV = () => {
    const rows = [...missingKeywords].sort((a, b) => (b.impressions || 0) - (a.impressions || 0));
    if (!rows.length) { flash("Немає упущених ключів для експорту"); return; }
    const esc = (s) => `"${String(s == null ? "" : s).replace(/[\r\n]+/g, " ").replace(/"/g, '""').trim()}"`;
    const header = String.fromCharCode(0xFEFF) + "Запит;Джерело;Тип;Сторінка (де ранжує);Релевантна сторінка;Частота;Кліки;Позиція\r\n";
    const body = rows.map((r) => {
      const matched = missingMatches.get(r.query) || "";
      const src = r.source === "gsc" ? "GSC" : "Serpstat";
      const type = r.isInfo ? "Блог/Інфо" : "Комерційний";
      const pos = typeof r.position === "number" ? String(Math.round(r.position * 10) / 10).replace(".", ",") : (r.position || "");
      return [esc(r.query), esc(src), esc(type), esc(r.page || ""), esc(matched), r.impressions || 0, r.clicks || 0, pos].join(";");
    }).join("\r\n");
    const domain = (proj && proj.domain ? proj.domain : "site").replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/[^\w.-]/g, "");
    dlFile(header + body, `upusheni-klyuchi_${domain || "site"}.csv`);
  };

  // Toggle checkbox
  const toggleCheck = (query) => {
    setCheckedMissing(prev => {
      const next = new Set(prev);
      next.has(query) ? next.delete(query) : next.add(query);
      return next;
    });
  };
  const toggleAllVisible = () => {
    const visible = (missingTab === "commercial" ? missingCommercial : missingBlog);
    const allChecked = visible.every(r => checkedMissing.has(r.query));
    setCheckedMissing(prev => {
      const next = new Set(prev);
      visible.forEach(r => allChecked ? next.delete(r.query) : next.add(r.query));
      return next;
    });
  };

  // Import selected keywords
  const handleImport = () => {
    const selected = missingKeywords.filter(r => checkedMissing.has(r.query));
    if (!selected.length) return;
    setImporting(true);

    // Use setTimeout to avoid blocking UI
    setTimeout(() => {
      const matched = [];
      const unmatched = [];
      // Batch URL matching with pre-built index
      const pageCache = new Map();
      selected.forEach(r => {
        let url = pageCache.get(r.page);
        if (url === undefined) { url = matchUrl(r.page); pageCache.set(r.page, url); }
        if (url) {
          matched.push({ keyword: r.query, frequency: r.impressions, url });
        } else {
          unmatched.push({ keyword: r.query, frequency: r.impressions, page: r.page });
        }
      });

      if (matched.length > 0) importKeywords(matched);

      if (unmatched.length > 0) {
        setUnmatchedKws(unmatched);
        const da = {};
        unmatched.forEach(u => { da[u.keyword] = ""; });
        setAssignUrl(da);
        setShowAssign(true);
      } else {
        setShowMissing(false);
        flash(`Добавлено ${matched.length} ключей в семантику`);
      }
      setImporting(false);
    }, 50);
  };

  const importKeywords = (items) => {
    const s = getStore(activeId);
    const updatedClusters = (s.clusters || []).map(c => ({ ...c }));
    const clusterByUrl = new Map();
    updatedClusters.forEach((c, i) => { clusterByUrl.set(c.url, i); });

    // Pre-build existing keyword sets per cluster for O(1) dedup
    const kwSets = updatedClusters.map(c => new Set((c.keywords || []).map(k => k.keyword.toLowerCase())));

    // Group items by target URL to minimize cluster copies
    const byUrl = new Map();
    items.forEach(item => {
      if (!byUrl.has(item.url)) byUrl.set(item.url, []);
      byUrl.get(item.url).push(item);
    });

    byUrl.forEach((kwItems, url) => {
      const idx = clusterByUrl.get(url);
      if (idx !== undefined) {
        const c = updatedClusters[idx];
        const kwSet = kwSets[idx];
        const newKws = [];
        kwItems.forEach(item => {
          if (!kwSet.has(item.keyword.toLowerCase())) {
            newKws.push({ keyword: item.keyword, frequency: item.frequency });
            kwSet.add(item.keyword.toLowerCase());
          }
        });
        if (newKws.length > 0) {
          c.keywords = [...(c.keywords || []), ...newKws];
          c.keywords.sort((a, b) => b.frequency - a.frequency);
          c.allCount = c.keywords.length;
          c.hfKeyword = c.keywords[0]?.keyword || "";
          c.hfFreq = c.keywords[0]?.frequency || 0;
        }
      } else {
        const kws = kwItems.map(item => ({ keyword: item.keyword, frequency: item.frequency }));
        kws.sort((a, b) => b.frequency - a.frequency);
        const nc = {
          id: uid(), url,
          keywords: kws, allCount: kws.length,
          hfKeyword: kws[0]?.keyword || "", hfFreq: kws[0]?.frequency || 0,
          status: kws.length >= 3 ? "READY" : "NOT_ENOUGH_KEYWORDS",
          meta: null, issues: [], error: "",
        };
        clusterByUrl.set(url, updatedClusters.length);
        kwSets.push(new Set(kws.map(k => k.keyword.toLowerCase())));
        updatedClusters.push(nc);
      }
    });

    const rows = updatedClusters.flatMap(c => (c.keywords || []).map(k => ({ keyword: k.keyword, frequency: k.frequency, url: c.url })));
    setStore(activeId, { ...s, rows, clusters: updatedClusters });
    saveClusters(activeId, updatedClusters);
    setTick(v => v + 1);
  };

  const handleAssignConfirm = () => {
    const items = unmatchedKws
      .filter(u => assignUrl[u.keyword])
      .map(u => ({ keyword: u.keyword, frequency: u.frequency, url: assignUrl[u.keyword] }));
    if (items.length > 0) importKeywords(items);
    const skipped = unmatchedKws.filter(u => !assignUrl[u.keyword]).length;
    setShowAssign(false);
    setShowMissing(false);
    flash(`Добавлено ${items.length} ключей${skipped ? `, пропущено ${skipped}` : ""}`);
  };

  // AI Content Brief
  const generateBrief = async () => {
    if (!filterUrl) return;
    setBriefLoading(true);
    setBriefData(null);
    setBriefProgress({ step: "Початок...", current: 0, total: 4 });
    try {
      const kws = allKeywords.filter(k => k.url === filterUrl).map(k => ({ keyword: k.keyword, frequency: k.frequency }));
      const resp = await fetch(`/api/projects/${activeId}/content-brief`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url: filterUrl, keywords: kws }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        flash("Помилка: " + (err.error || resp.statusText));
        setBriefLoading(false);
        setBriefProgress(null);
        return;
      }
      const text = await resp.text();
      const lines = text.split("\n");
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const msg = JSON.parse(line.slice(6));
          if (msg.type === "progress") setBriefProgress(msg);
          else if (msg.type === "result") setBriefData(msg);
          else if (msg.type === "done") setBriefProgress(null);
        } catch {}
      }
    } catch (e) { flash("Помилка: " + e.message); }
    setBriefLoading(false);
    setBriefProgress(null);
  };

  const tabStyle = (active) => ({
    padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", border: "none",
    borderBottom: active ? "2px solid #6366f1" : "2px solid transparent",
    background: "none", color: active ? "#6366f1" : t.textMuted, fontFamily: "inherit",
  });

  const handlePasteImport = (mergeMode = false) => {
    setPasteErr("");
    if (!pasteText.trim()) { setPasteErr("Порожній текст"); return; }
    const { rows, skipped, noUrl } = parsePastedRows(pasteText);
    if (!rows.length) { setPasteErr(`Не вдалося розпарсити (${skipped} без ключа/частотності, ${noUrl} без URL). Формат: ключ [TAB] частотність [TAB] url`); return; }
    if (noUrl) flash(`⚠️ ${noUrl} ключів без URL — пропущено (не додано на головну)`);
    if (mergeMode) {
      const existing = getStore(activeId).clusters || [];
      const { clusters: merged, newUrls, updatedUrls } = mergeClusters(existing, rows);
      const allRows = merged.flatMap(c => (c.keywords || []).map(k => ({ keyword: k.keyword, frequency: k.frequency, url: c.url })));
      setStore(activeId, { ...getStore(activeId), rows: allRows, clusters: merged });
      setProjects(ps => ps.map(p => p.id === activeId ? { ...p, cc: merged.length, rc: allRows.length } : p));
      apiFetch(`/api/projects/${activeId}`, { method: "PUT", body: JSON.stringify({ cc: merged.length }) }).catch(() => {});
      saveClusters(activeId, merged);
      flash(`Доповнено: +${newUrls} нових URL, ${updatedUrls} оновлено, ${rows.length} ключів`);
    } else {
      const built = buildClusters(rows);
      setStore(activeId, { ...getStore(activeId), rows, clusters: built });
      setProjects(ps => ps.map(p => p.id === activeId ? { ...p, cc: built.length, rc: rows.length } : p));
      apiFetch(`/api/projects/${activeId}`, { method: "PUT", body: JSON.stringify({ cc: built.length }) }).catch(() => {});
      saveClusters(activeId, built);
      flash(`${rows.length} ключів → ${built.length} кластерів`);
    }
    setPasteText("");
    setPasteMode(false);
  };

  const toggleSelectKw = (keyword, url) => {
    const key = keyword + "|||" + url;
    setSelectedKws(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const selectAllVisible = () => {
    if (selectedKws.size === pagedFiltered.length) {
      setSelectedKws(new Set());
    } else {
      setSelectedKws(new Set(pagedFiltered.map(k => k.keyword + "|||" + k.url)));
    }
  };

  const deleteSelected = () => {
    if (!selectedKws.size) return;
    if (!window.confirm(`Видалити ${selectedKws.size} ключів?`)) return;
    const toDelete = new Set(selectedKws);
    const s = getStore(activeId);
    const updated = (s.clusters || []).map(c => {
      const newKws = (c.keywords || []).filter(k => {
        const kw = typeof k === "string" ? k : k.keyword;
        return !toDelete.has(kw + "|||" + c.url);
      });
      if (newKws.length === c.keywords?.length) return c;
      const hf = newKws.length ? newKws.reduce((max, k) => {
        const freq = typeof k === "string" ? 0 : k.frequency || 0;
        return freq > (max.frequency || 0) ? { keyword: typeof k === "string" ? k : k.keyword, frequency: freq } : max;
      }, { keyword: "", frequency: 0 }) : { keyword: "", frequency: 0 };
      return { ...c, keywords: newKws, allCount: newKws.length, hfKeyword: hf.keyword, hfFreq: hf.frequency };
    }).filter(c => c.keywords.length > 0);
    const allRows = updated.flatMap(c => (c.keywords || []).map(k => ({ keyword: typeof k === "string" ? k : k.keyword, frequency: typeof k === "string" ? 0 : k.frequency || 0, url: c.url })));
    setStore(activeId, { ...s, rows: allRows, clusters: updated });
    saveClusters(activeId, updated);
    flash(`Видалено ${selectedKws.size} ключів`);
    setSelectedKws(new Set());
    setTick(v => v + 1);
  };

  const tagSelected = (tag) => {
    if (!selectedKws.size || !tag.trim()) return;
    const toTag = new Set(selectedKws);
    const s = getStore(activeId);
    const updated = (s.clusters || []).map(c => {
      const newKws = (c.keywords || []).map(k => {
        const kw = typeof k === "string" ? k : k.keyword;
        if (!toTag.has(kw + "|||" + c.url)) return k;
        const existing = typeof k === "string" ? { keyword: k, frequency: 0 } : { ...k };
        const tags = existing.tags ? existing.tags.split(",").map(t => t.trim()).filter(Boolean) : [];
        if (!tags.includes(tag.trim())) tags.push(tag.trim());
        return { ...existing, tags: tags.join(",") };
      });
      return { ...c, keywords: newKws };
    });
    setStore(activeId, { ...s, clusters: updated });
    saveClusters(activeId, updated);
    flash(`Тег "${tag}" додано до ${selectedKws.size} ключів`);
    setSelectedKws(new Set());
    setTagInput("");
    setTick(v => v + 1);
  };

  const removeTagFromSelected = (tag) => {
    if (!selectedKws.size) return;
    const toUntag = new Set(selectedKws);
    const s = getStore(activeId);
    const updated = (s.clusters || []).map(c => {
      const newKws = (c.keywords || []).map(k => {
        const kw = typeof k === "string" ? k : k.keyword;
        if (!toUntag.has(kw + "|||" + c.url)) return k;
        if (typeof k === "string") return k;
        const tags = (k.tags || "").split(",").map(t => t.trim()).filter(t => t && t !== tag);
        return { ...k, tags: tags.join(",") };
      });
      return { ...c, keywords: newKws };
    });
    setStore(activeId, { ...s, clusters: updated });
    saveClusters(activeId, updated);
    flash(`Тег "${tag}" знято з ${selectedKws.size} ключів`);
    setSelectedKws(new Set());
    setTick(v => v + 1);
  };

  // Collect all unique tags
  const allTags = useMemo(() => {
    const tags = new Set();
    clusters.forEach(c => (c.keywords || []).forEach(k => {
      if (typeof k !== "string" && k.tags) k.tags.split(",").forEach(t => { if (t.trim()) tags.add(t.trim()); });
    }));
    return [...tags].sort();
  }, [clusters, tick]);

  if (!proj) return null;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>Семантика</h2>
        <button style={{ ...btnSm, color: "#dc2626", borderColor: "#dc2626", marginLeft: "auto" }} onClick={() => {
          if (!window.confirm("Видалити всю семантику? Це незворотня дія.")) return;
          setStore(activeId, { ...getStore(activeId), clusters: [], rows: [] });
          saveClusters(activeId, []);
          flash("Семантику видалено");
        }}>{Icons.trash(14)} Видалити семантику</button>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { label: "Ключей", val: totalKw },
          { label: "Суммарная частотность", val: totalFreq.toLocaleString() },
          { label: "Страниц", val: clusters.length },
          { label: "Meta", val: `${metaPages}/${clusters.length}` },
          { label: "Контент", val: `${contentPages}/${clusters.length}` },
        ].map((s, i) => (
          <div key={i} style={{ ...card, flex: 1, minWidth: 120, padding: 12, marginBottom: 0 }}>
            <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, marginBottom: 2 }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Tabs: Main / Blog */}
      <div style={{ display: "flex", borderBottom: `1px solid ${t.border}`, marginBottom: 14 }}>
        <button style={tabStyle(semTab === "main")} onClick={() => setSemTab("main")}>
          Коммерческие ({mainKw.length})
        </button>
        <button style={tabStyle(semTab === "blog")} onClick={() => setSemTab("blog")}>
          Блог / Инфо ({blogKw.length})
        </button>
        <div style={{ flex: 1 }} />
        {(gscRows || serpstatRows) ? (
          <button
            style={{ ...btnP, fontSize: 12, padding: "6px 14px", alignSelf: "center", marginBottom: 4 }}
            onClick={() => { setShowMissing(true); setMissingTab(semTab === "blog" ? "blog" : "commercial"); }}
          >
            Упущені ключі ({missingKeywords.length})
          </button>
        ) : (
          <span style={{ fontSize: 12, color: t.textMuted, alignSelf: "center", marginBottom: 4 }}>
            Підключіть GSC або Serpstat щоб знайти упущені ключі
          </span>
        )}
      </div>

      {/* Filters */}
      <div style={{ ...card, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 4 }}>Пошук по ключу</label>
          <input
            value={kwSearch} onChange={e => { setKwSearch(e.target.value); setPage(0); }}
            placeholder="Введіть ключ..."
            style={{ ...inp, width: 220, fontSize: 12, padding: "6px 10px" }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, display: "block", marginBottom: 4 }}>Фільтр по URL</label>
          <input
            type="text" value={filterUrl} onChange={e => setFilterUrl(e.target.value)}
            placeholder={`Пошук по URL (всього: ${clusters.length})`}
            style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 12, background: t.bgCard, color: t.text, fontFamily: "inherit", minWidth: 280 }}
          />
        </div>
        <div style={{ alignSelf: "flex-end", display: "flex", gap: 6 }}>
          <button style={btnSm} onClick={checkAllUrls}>Перевірити коди</button>
          {filterUrl && (
            <button style={{ ...btnP, fontSize: 11, padding: "6px 14px" }} onClick={generateBrief} disabled={briefLoading}>
              {briefLoading ? `${briefProgress?.step || "..."}` : "AI Brief"}
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {selectedKws.size > 0 && (
        <div style={{ ...card, display: "flex", gap: 10, alignItems: "center", padding: "10px 16px", flexWrap: "wrap", background: dark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.04)", border: `1px solid ${dark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.1)"}` }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#6366f1" }}>Обрано: {selectedKws.size}</span>
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <input
              value={tagInput} onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && tagInput.trim()) tagSelected(tagInput); }}
              placeholder="Назва тегу..."
              style={{ ...inp, width: 140, fontSize: 11, padding: "4px 8px" }}
            />
            <button style={{ ...btnP, fontSize: 11, padding: "4px 10px" }} onClick={() => tagSelected(tagInput)} disabled={!tagInput.trim()}>{Icons.tag(14)} Тег</button>
          </div>
          {allTags.length > 0 && (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {allTags.map(tag => (
                <button key={tag} onClick={() => tagSelected(tag)} style={{ fontSize: 10, padding: "3px 8px", borderRadius: 6, border: `1px solid ${dark ? "#444" : "#ddd"}`, background: dark ? "#252535" : "#f3f4f6", color: t.text, cursor: "pointer", fontFamily: "inherit" }}>
                  +{tag}
                </button>
              ))}
            </div>
          )}
          <button style={{ ...btnSm, color: "#dc2626", borderColor: "#dc262633" }} onClick={deleteSelected}>{Icons.trash(14)} Видалити</button>
          <button style={btnSm} onClick={() => setSelectedKws(new Set())}>Скасувати</button>
        </div>
      )}

      {/* Table */}
      <div style={{ ...card, padding: 0, overflow: "auto" }}>
        <div style={{ padding: "12px 18px", borderBottom: `1px solid ${t.border}`, fontSize: 13, fontWeight: 600 }}>
          {filtered.length} ключей {filterUrl && `для ${stripDomain(filterUrl)}`}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: 30, textAlign: "center", cursor: "default" }}>
                <input type="checkbox" checked={selectedKws.size > 0 && selectedKws.size === pagedFiltered.length} onChange={selectAllVisible} style={{ cursor: "pointer" }} />
              </th>
              <th style={thStyle} onClick={() => toggleSort("keyword")}>Ключ{sortIcon("keyword")}</th>
              <th style={{ ...thStyle, textAlign: "right", width: 100 }} onClick={() => toggleSort("frequency")}>Частотність{sortIcon("frequency")}</th>
              <th style={thStyle} onClick={() => toggleSort("url")}>URL{sortIcon("url")}</th>
              <th style={thStyle}>H1</th>
              <th style={{ ...thStyle, textAlign: "center", width: 60 }}>Meta</th>
              <th style={{ ...thStyle, textAlign: "center", width: 70 }}>Контент</th>
              <th style={{ ...thStyle, textAlign: "center", width: 60 }}>HTTP</th>
            </tr>
          </thead>
          <tbody>
            {pagedFiltered.map((k, i) => (
              <tr key={i} style={{ background: selectedKws.has(k.keyword + "|||" + k.url) ? (dark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.04)") : i % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)") }}>
                <td style={{ ...tdStyle, textAlign: "center", width: 30 }}>
                  <input type="checkbox" checked={selectedKws.has(k.keyword + "|||" + k.url)} onChange={() => toggleSelectKw(k.keyword, k.url)} style={{ cursor: "pointer" }} />
                </td>
                <td style={tdStyle}>
                  {k.keyword}
                  {k.isExpansion && <span style={{ marginLeft: 6, fontSize: 9, background: "#e0e7ff", color: "#4338ca", padding: "1px 5px", borderRadius: 6, fontWeight: 600 }}>расш.</span>}
                  {k.tags && k.tags.split(",").map(tag => tag.trim()).filter(Boolean).map(tag => (
                    <span key={tag} style={{ marginLeft: 4, fontSize: 9, background: dark ? "rgba(139,92,246,0.15)" : "#ede9fe", color: "#7c3aed", padding: "1px 6px", borderRadius: 6, fontWeight: 600 }}>{tag}</span>
                  ))}
                </td>
                <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{k.frequency}</td>
                <td style={{ ...tdStyle, maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={k.url}>{stripDomain(k.url)}{k.url && (userRole === "admin" || userRole === "user") && <span onClick={e => { e.stopPropagation(); openRename(k.url); }} title="Змінити URL сторінки (каскадно)" style={{ marginLeft: 6, fontSize: 11, cursor: "pointer", opacity: 0.5 }}>✎</span>}</td>
                <td style={{ ...tdStyle, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11, color: t.textMuted }} title={k.h1}>{k.h1 || "—"}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  {k.hasMeta
                    ? <span style={{ background: "#d1fae5", color: "#065f46", padding: "1px 7px", borderRadius: 8, fontWeight: 600, fontSize: 11 }}>✓</span>
                    : <span style={{ background: dark ? "#252535" : "#f3f4f6", color: dark ? "#555" : "#aaa", padding: "1px 7px", borderRadius: 8, fontSize: 11 }}>—</span>}
                </td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  {k.hasContent
                    ? <span style={{ background: "#d1fae5", color: "#065f46", padding: "1px 7px", borderRadius: 8, fontWeight: 600, fontSize: 11 }}>✓</span>
                    : <span style={{ background: dark ? "#252535" : "#f3f4f6", color: dark ? "#555" : "#aaa", padding: "1px 7px", borderRadius: 8, fontSize: 11 }}>—</span>}
                </td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  {k.httpCode != null
                    ? <span style={{ ...codeBg(k.httpCode), padding: "1px 7px", borderRadius: 8, fontWeight: 600, fontSize: 11 }}>{k.httpCode}</span>
                    : <span style={{ background: dark ? "#252535" : "#f3f4f6", color: dark ? "#555" : "#aaa", padding: "1px 7px", borderRadius: 8, fontSize: 11 }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 18px", borderTop: `1px solid ${t.border}` }}>
            <button disabled={page === 0} onClick={() => setPage(0)} style={{ ...btnSm, opacity: page === 0 ? 0.4 : 1 }}>«</button>
            <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{ ...btnSm, opacity: page === 0 ? 0.4 : 1 }}>‹</button>
            <span style={{ fontSize: 12, color: t.textMuted }}>
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} з {filtered.length}
            </span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} style={{ ...btnSm, opacity: page >= totalPages - 1 ? 0.4 : 1 }}>›</button>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)} style={{ ...btnSm, opacity: page >= totalPages - 1 ? 0.4 : 1 }}>»</button>
          </div>
        )}
      </div>

      {/* AI Content Brief + Quick Wins Modal */}
      {briefData && briefData.brief && (() => {
        const b = briefData.brief;
        const g = briefData.gsc || {};
        const comps = briefData.competitors || [];
        const priorityColor = { high: "#ef4444", medium: "#f59e0b", low: "#6366f1" };
        const row = (label, current, recommended, ok) => (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f3f4f6"}` }}>
            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: ok ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)", color: ok ? "#10b981" : "#f59e0b", flexShrink: 0 }}>{ok ? "OK" : "FIX"}</span>
            <span style={{ fontSize: 11, fontWeight: 600, width: 120, flexShrink: 0, color: t.textMuted }}>{label}</span>
            <div style={{ flex: 1, fontSize: 12 }}>
              {current && <div style={{ color: t.textMuted, marginBottom: 2 }}>Зараз: {current}</div>}
              {recommended && <div style={{ color: t.text, fontWeight: 500 }}>{recommended}</div>}
            </div>
          </div>
        );
        return (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 40 }} onClick={e => { if (e.target === e.currentTarget) setBriefData(null); }}>
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", width: "90%", maxWidth: 800, maxHeight: "calc(100vh - 80px)", overflow: "auto", background: t.bgCard, borderRadius: 16, padding: "24px 28px", border: `1px solid ${dark ? "rgba(99,102,241,0.3)" : "rgba(99,102,241,0.15)"}`, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            {/* Header with GSC metrics */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ fontSize: 14, fontWeight: 700 }}>AI Brief + Quick Wins</span>
              {g.isQuickWin && <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 8, background: "rgba(239,68,68,0.1)", color: "#ef4444", animation: "pulse 1.5s infinite" }}>QUICK WIN</span>}
              <span style={{ fontSize: 11, color: t.textMuted, marginLeft: "auto" }}>{stripDomain(filterUrl)}</span>
              <button onClick={() => setBriefData(null)} style={{ ...btnSm, fontSize: 10 }}>x</button>
            </div>

            {/* GSC Stats bar */}
            {g.avgPos > 0 && (
              <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                {[
                  { label: "Позиція", value: g.avgPos?.toFixed(1), color: g.avgPos <= 10 ? "#10b981" : g.avgPos <= 20 ? "#f59e0b" : "#ef4444" },
                  { label: "Покази/міс", value: g.totalImpr?.toLocaleString() },
                  { label: "Кліки/міс", value: g.totalClicks?.toLocaleString() },
                  { label: "Внутр. посилань", value: briefData.internalLinksTo, color: briefData.internalLinksTo >= 5 ? "#10b981" : "#ef4444" },
                  { label: "ROI Score", value: g.roiScore, color: g.roiScore >= 50 ? "#10b981" : "#f59e0b" },
                ].map((s, i) => (
                  <div key={i} style={{ background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", borderRadius: 10, padding: "8px 14px", minWidth: 80 }}>
                    <div style={{ fontSize: 9, color: t.textMuted, fontWeight: 600, textTransform: "uppercase" }}>{s.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: s.color || t.text }}>{s.value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Action Plan — priority sorted */}
            {b.actions && b.actions.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Action Plan</div>
                {b.actions.map((a, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, padding: "10px 12px", marginBottom: 4, borderRadius: 10, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)", border: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` }}>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: `${priorityColor[a.priority] || "#6366f1"}18`, color: priorityColor[a.priority] || "#6366f1", flexShrink: 0, alignSelf: "flex-start" }}>{a.priority === "high" ? "HIGH" : a.priority === "medium" ? "MED" : "LOW"}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{a.action}</div>
                      <div style={{ fontSize: 11, color: t.textMuted }}>{a.reason}</div>
                      {a.impact && <div style={{ fontSize: 10, color: "#10b981", marginTop: 2 }}>{a.impact}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Meta recommendations */}
            {row("Обсяг тексту", `${b.currentWords || briefData.ourPage?.wordCount || 0} слів`, `мін. ${b.minWords} (серед. ТОП: ${b.competitorAvg?.words || "?"})`, (b.currentWords || briefData.ourPage?.wordCount || 0) >= (b.minWords || 0))}

            {/* H2 sections */}
            {((b.requiredH2 && b.requiredH2.length > 0) || (b.missingH2 && b.missingH2.length > 0)) && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", marginBottom: 6 }}>Структура H2</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(b.requiredH2 || []).map((h, i) => {
                    const has = (briefData.ourPage?.h2s || []).some(oh => oh.toLowerCase().includes(h.toLowerCase().slice(0, 10)));
                    return <span key={"r" + i} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 8, background: has ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)", color: has ? "#10b981" : "#f59e0b", fontWeight: 600 }}>{has ? "✓" : "+"} {h}</span>;
                  })}
                  {(b.missingH2 || []).map((h, i) => <span key={"m" + i} style={{ fontSize: 11, padding: "4px 10px", borderRadius: 8, background: "rgba(239,68,68,0.08)", color: "#ef4444", fontWeight: 600 }}>+ {h}</span>)}
                </div>
              </div>
            )}

            {/* FAQ + Internal Links + Advantages in columns */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
              {b.faqQuestions && b.faqQuestions.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", marginBottom: 6 }}>FAQ</div>
                  {b.faqQuestions.map((q, i) => <div key={i} style={{ fontSize: 11, padding: "2px 0", color: t.text }}>• {q}</div>)}
                </div>
              )}
              {b.internalLinks && b.internalLinks.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", marginBottom: 6 }}>Додати посилання</div>
                  {b.internalLinks.map((l, i) => (
                    <div key={i} style={{ fontSize: 11, padding: "2px 0" }}>
                      <span style={{ color: "#6366f1" }}>{stripDomain(l.url)}</span> <span style={{ color: t.textMuted }}>"{l.anchor}"</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {b.advantages && b.advantages.length > 0 && (
              <div style={{ marginTop: 10, background: dark ? "rgba(16,185,129,0.04)" : "rgba(16,185,129,0.03)", borderRadius: 10, padding: "10px 14px", border: `1px solid ${dark ? "rgba(16,185,129,0.1)" : "rgba(16,185,129,0.06)"}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#10b981", marginBottom: 6, textTransform: "uppercase" }}>Ваша перевага</div>
                {b.advantages.map((a, i) => <div key={i} style={{ fontSize: 12, padding: "2px 0", color: t.text }}>★ {a}</div>)}
              </div>
            )}

            {/* Competitors */}
            {comps.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 11 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", marginBottom: 6 }}>Конкуренти ТОП-10</div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr", gap: 4, fontSize: 10 }}>
                  <div style={{ fontWeight: 700, color: t.textMuted }}>Домен</div>
                  <div style={{ fontWeight: 700, color: t.textMuted }}>Слів</div>
                  <div style={{ fontWeight: 700, color: t.textMuted }}>H2</div>
                  <div style={{ fontWeight: 700, color: t.textMuted }}>FAQ</div>
                  <div style={{ fontWeight: 700, color: t.textMuted }}>Schema</div>
                  {comps.map((c, i) => (
                    <React.Fragment key={i}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.url}>{new URL(c.url).hostname}</div>
                      <div>{c.wordCount}</div>
                      <div>{c.h2Count}</div>
                      <div>{c.hasFAQ ? "✓" : "—"}</div>
                      <div>{c.hasSchema ? "✓" : "—"}</div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* Top GSC queries */}
            {g.topQueries && g.topQueries.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 11 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", marginBottom: 6 }}>ТОП запити GSC по цій сторінці</div>
                {g.topQueries.slice(0, 8).map((q, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, padding: "3px 0", borderBottom: `1px solid ${dark ? "#1e1e2c" : "#f7f7f7"}` }}>
                    <span style={{ flex: 1 }}>{q.query}</span>
                    <span style={{ color: q.position <= 10 ? "#10b981" : q.position <= 20 ? "#f59e0b" : "#ef4444", fontWeight: 700, width: 40, textAlign: "right" }}>{q.position.toFixed(1)}</span>
                    <span style={{ color: t.textMuted, width: 60, textAlign: "right" }}>{q.impressions} пок.</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        );
      })()}

      {/* Missing Keywords Modal */}
      {showMissing && (() => {
        const byTab = missingTab === "commercial" ? missingCommercial : missingBlog;
        const filtered = missingSource === "all" ? byTab : byTab.filter(r => r.source === missingSource);
        const dir = missingSortDir === "asc" ? 1 : -1;
        const cmpStr = (a, b) => (a || "").toString().localeCompare((b || "").toString(), undefined, { sensitivity: "base", numeric: true });
        const cmpNum = (a, b) => (Number.isFinite(+a) ? +a : 0) - (Number.isFinite(+b) ? +b : 0);
        const visible = [...filtered].sort((a, b) => {
          switch (missingSortCol) {
            case "query": return dir * cmpStr(a.query, b.query);
            case "source": return dir * cmpStr(a.source, b.source);
            case "page": return dir * cmpStr(a.page, b.page);
            case "clicks": return dir * cmpNum(a.clicks, b.clicks);
            case "position": return dir * cmpNum(a.position, b.position);
            case "matched": {
              const am = missingMatches.get(a.query) ? 1 : 0;
              const bm = missingMatches.get(b.query) ? 1 : 0;
              return dir * (am - bm);
            }
            case "impressions":
            default: return dir * cmpNum(a.impressions, b.impressions);
          }
        });
        const visibleChecked = visible.filter(r => checkedMissing.has(r.query)).length;
        const totalChecked = missingKeywords.filter(r => checkedMissing.has(r.query)).length;
        const gscCount = byTab.filter(r => r.source === "gsc").length;
        const ssCount = byTab.filter(r => r.source === "serpstat").length;
        return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowMissing(false); }}>
          <div style={{ background: t.bgCard, borderRadius: 16, width: "90%", maxWidth: 950, maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
            <div style={{ padding: "18px 24px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 12 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Упущені ключі</h3>
              <span style={{ fontSize: 12, color: t.textMuted }}>ключі яких немає в семантиці</span>
              <div style={{ flex: 1 }} />
              <button style={{ ...btnSm, fontSize: 12 }} onClick={exportMissingCSV} title="Експорт усіх упущених ключів у Excel">⬇ Excel</button>
              {totalChecked > 0 && (
                <button style={{ ...btnP, fontSize: 12, padding: "6px 16px", opacity: importing ? 0.6 : 1 }} onClick={handleImport} disabled={importing}>
                  {importing ? "Загрузка..." : `Завантажити обрані (${totalChecked})`}
                </button>
              )}
              <button onClick={() => setShowMissing(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: t.textMuted, padding: 4 }}>{Icons.close(18)}</button>
            </div>
            <div style={{ borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", flexWrap: "wrap" }}>
              <button style={tabStyle(missingTab === "commercial")} onClick={() => { setMissingTab("commercial"); setMissingPage(0); }}>
                Комерційні ({missingCommercial.length})
              </button>
              <button style={tabStyle(missingTab === "blog")} onClick={() => { setMissingTab("blog"); setMissingPage(0); }}>
                Блог / Інфо ({missingBlog.length})
              </button>
              <div style={{ marginLeft: "auto", display: "flex", gap: 4, padding: "6px 12px" }}>
                {[
                  { key: "all", label: "Всі", count: byTab.length, bg: dark ? "#333" : "#e5e7eb", color: dark ? "#ccc" : "#374151" },
                  { key: "gsc", label: "GSC", count: gscCount, bg: "#dbeafe", color: "#1e40af" },
                  { key: "serpstat", label: "Serpstat", count: ssCount, bg: "#ede9fe", color: "#6d28d9" },
                ].map(f => (
                  <button key={f.key} onClick={() => { setMissingSource(f.key); setMissingPage(0); }}
                    style={{ padding: "3px 10px", borderRadius: 6, border: missingSource === f.key ? "2px solid" : "1px solid transparent", borderColor: missingSource === f.key ? f.color : "transparent", background: f.bg, color: f.color, fontSize: 11, fontWeight: 600, cursor: "pointer", opacity: missingSource === f.key ? 1 : 0.6 }}>
                    {f.label} ({f.count})
                  </button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: 0 }}>
              {/* Pagination controls top */}
              {visible.length > MISSING_PAGE_SIZE && (
                <div style={{ padding: "8px 18px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                  <span style={{ color: t.textMuted }}>{visible.length} ключей, стр. {missingPage + 1} / {Math.ceil(visible.length / MISSING_PAGE_SIZE)}</span>
                  <div style={{ flex: 1 }} />
                  <button style={btnSm} disabled={missingPage === 0} onClick={() => setMissingPage(p => p - 1)}>← Назад</button>
                  <button style={btnSm} disabled={(missingPage + 1) * MISSING_PAGE_SIZE >= visible.length} onClick={() => setMissingPage(p => p + 1)}>Вперёд →</button>
                </div>
              )}
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...thStyle, width: 36, position: "sticky", top: 0, background: t.bgCard, cursor: "pointer" }} onClick={toggleAllVisible}>
                      <input type="checkbox" checked={visible.length > 0 && visible.every(r => checkedMissing.has(r.query))} onChange={toggleAllVisible}
                        style={{ cursor: "pointer", width: 16, height: 16 }} />
                    </th>
                    <th style={{ ...thStyle, position: "sticky", top: 0, background: t.bgCard, cursor: "pointer", userSelect: "none" }} onClick={() => toggleMissingSort("query")} title="Сортувати за запитом">Запит{missingSortIcon("query")}</th>
                    <th style={{ ...thStyle, width: 70, textAlign: "center", position: "sticky", top: 0, background: t.bgCard, cursor: "pointer", userSelect: "none" }} onClick={() => toggleMissingSort("source")} title="Сортувати за джерелом">Джерело{missingSortIcon("source")}</th>
                    <th style={{ ...thStyle, position: "sticky", top: 0, background: t.bgCard, maxWidth: 200, cursor: "pointer", userSelect: "none" }} onClick={() => toggleMissingSort("page")} title="Сортувати за сторінкою">Сторінка{missingSortIcon("page")}</th>
                    <th style={{ ...thStyle, textAlign: "right", width: 90, position: "sticky", top: 0, background: t.bgCard, cursor: "pointer", userSelect: "none" }} onClick={() => toggleMissingSort("impressions")} title="Сортувати за частотою">Частота{missingSortIcon("impressions")}</th>
                    <th style={{ ...thStyle, textAlign: "right", width: 70, position: "sticky", top: 0, background: t.bgCard, cursor: "pointer", userSelect: "none" }} onClick={() => toggleMissingSort("clicks")} title="Сортувати за кліками">Кліки{missingSortIcon("clicks")}</th>
                    <th style={{ ...thStyle, textAlign: "right", width: 70, position: "sticky", top: 0, background: t.bgCard, cursor: "pointer", userSelect: "none" }} onClick={() => toggleMissingSort("position")} title="Сортувати за позицією">Позиція{missingSortIcon("position")}</th>
                    <th style={{ ...thStyle, width: 60, textAlign: "center", position: "sticky", top: 0, background: t.bgCard, cursor: "pointer", userSelect: "none" }} onClick={() => toggleMissingSort("matched")} title="Сортувати за збігом">Збіг{missingSortIcon("matched")}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.slice(missingPage * MISSING_PAGE_SIZE, (missingPage + 1) * MISSING_PAGE_SIZE).map((r, i) => {
                    const matched = missingMatches.get(r.query);
                    const idx = missingPage * MISSING_PAGE_SIZE + i;
                    return (
                      <tr key={idx}
                        onClick={() => toggleCheck(r.query)}
                        style={{ background: checkedMissing.has(r.query) ? (dark ? "rgba(99,102,241,.15)" : "rgba(99,102,241,.06)") : (idx % 2 === 0 ? "transparent" : (dark ? "rgba(255,255,255,.02)" : "rgba(0,0,0,.015)")), cursor: "pointer" }}>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <input type="checkbox" checked={checkedMissing.has(r.query)} onChange={() => toggleCheck(r.query)}
                            style={{ cursor: "pointer", width: 16, height: 16 }} onClick={e => e.stopPropagation()} />
                        </td>
                        <td style={tdStyle}>{r.query}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 6, fontWeight: 600, background: r.source === "gsc" ? "#dbeafe" : "#ede9fe", color: r.source === "gsc" ? "#1e40af" : "#6d28d9" }}>
                            {r.source === "gsc" ? "GSC" : "SS"}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11, color: t.textMuted }} title={r.page}>
                          {r.page ? stripDomain(r.page) : "—"}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{r.impressions}</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>{r.clicks || 0}</td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>{typeof r.position === "number" ? Math.round(r.position * 10) / 10 : r.position}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          {matched
                            ? <span style={{ background: "#d1fae5", color: "#065f46", padding: "1px 7px", borderRadius: 8, fontWeight: 600, fontSize: 10 }} title={stripDomain(matched)}>✓</span>
                            : <span style={{ background: "#fef3c7", color: "#92400e", padding: "1px 7px", borderRadius: 8, fontSize: 10 }}>?</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {visible.length === 0 && (
                    <tr><td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: t.textMuted, padding: 30 }}>Немає упущених ключів</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Assign URL Modal for unmatched keywords */}
      {showAssign && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowAssign(false); }}>
          <div style={{ background: t.bgCard, borderRadius: 16, width: "90%", maxWidth: 750, maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,.4)" }}>
            <div style={{ padding: "18px 24px", borderBottom: `1px solid ${t.border}` }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 6px" }}>Назначить URL для ключей</h3>
              <p style={{ fontSize: 12, color: t.textMuted, margin: 0 }}>
                Эти ключи не удалось автоматически привязать к существующим страницам. Выберите URL для каждого или пропустите.
              </p>
            </div>
            <div style={{ flex: 1, overflow: "auto", padding: "12px 24px" }}>
              {unmatchedKws.map((u, i) => (
                <div key={i} style={{ ...card, display: "flex", alignItems: "center", gap: 14, padding: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{u.keyword}</div>
                    <div style={{ fontSize: 11, color: t.textMuted }}>
                      Частотность: {u.frequency}
                      {u.page && <> &middot; GSC страница: <span title={u.page}>{(() => { try { return new URL(u.page).pathname; } catch { return u.page; } })()}</span></>}
                    </div>
                  </div>
                  <select
                    value={assignUrl[u.keyword] || ""}
                    onChange={e => setAssignUrl(prev => ({ ...prev, [u.keyword]: e.target.value }))}
                    style={{ border: `1px solid ${t.border}`, borderRadius: 8, padding: "6px 10px", fontSize: 12, background: t.bgCard, color: t.text, fontFamily: "inherit", minWidth: 280, maxWidth: 350 }}
                  >
                    <option value="">Пропустить</option>
                    {urls.map(url => (
                      <option key={url} value={url}>{stripDomain(url)}</option>
                    ))}
                    {u.page && !urls.includes(u.page) && (
                      <option value={u.page}>+ Новая: {(() => { try { return new URL(u.page).pathname; } catch { return u.page; } })()}</option>
                    )}
                  </select>
                </div>
              ))}
            </div>
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${t.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button style={btnG} onClick={() => setShowAssign(false)}>Отмена</button>
              <button style={btnP} onClick={handleAssignConfirm}>
                Подтвердить
              </button>
            </div>
          </div>
        </div>
      )}
      {rn && (
        <div onClick={() => !rn.busy && setRn(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 560, maxWidth: "100%", background: dark ? "#17171f" : "#fff", border: `1px solid ${t.border}`, borderRadius: 14, padding: 22 }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4, color: t.text }}>Змінити URL сторінки</div>
            <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14 }}>Каскадно оновить: семантику, структуру, кокон (+ посилання на цю сторінку в інших блоках), тексти (+ посилання всередині), мету, schema, позиції.</div>
            <label style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Старий URL</label>
            <input value={rn.oldUrl} onChange={e => setRn(r => ({ ...r, oldUrl: e.target.value, preview: null }))} style={{ width: "100%", padding: "8px 10px", margin: "3px 0 10px", borderRadius: 8, border: `1px solid ${t.border}`, background: dark ? "#0f0f14" : "#fff", color: t.text, boxSizing: "border-box", fontSize: 13 }} />
            <label style={{ fontSize: 11, fontWeight: 600, color: t.textMuted }}>Новий URL</label>
            <input value={rn.newUrl} onChange={e => setRn(r => ({ ...r, newUrl: e.target.value, preview: null }))} placeholder="https://..." style={{ width: "100%", padding: "8px 10px", margin: "3px 0 10px", borderRadius: 8, border: `1px solid #6366f1`, background: dark ? "#0f0f14" : "#fff", color: t.text, boxSizing: "border-box", fontSize: 13 }} />
            <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: t.text, marginBottom: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={rn.mode === "prefix"} onChange={e => setRn(r => ({ ...r, mode: e.target.checked ? "prefix" : "single", preview: null }))} />
              Масова заміна: оновити й усі дочірні сторінки (гілку <code>{rnStrip(rn.oldUrl)}/*</code>)
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: t.text, marginBottom: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={rn.republish} onChange={e => setRn(r => ({ ...r, republish: e.target.checked }))} />
              Одразу переопублікувати на сайт (SDK)
            </label>
            {rn.preview && (
              <div style={{ background: dark ? "#0f0f14" : "#f8f9fb", border: `1px solid ${t.border}`, borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 11.5, color: t.text }}>
                <b>Буде оновлено:</b> {Object.entries(rn.preview).filter(([, v]) => Number(v) > 0).map(([k, v]) => `${k}: ${v}`).join(" · ") || "нічого не знайдено (перевірте URL)"}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setRn(null)} disabled={rn.busy} style={{ ...btnG, padding: "8px 14px" }}>Скасувати</button>
              <button onClick={rnPreview} disabled={rn.busy} style={{ ...btnG, padding: "8px 14px" }}>{rn.busy ? "…" : "Перевірити"}</button>
              <button onClick={rnApply} disabled={rn.busy || rnStrip(rn.oldUrl) === rnStrip(rn.newUrl)} style={{ ...btnP, padding: "8px 16px", opacity: (rn.busy || rnStrip(rn.oldUrl) === rnStrip(rn.newUrl)) ? 0.6 : 1 }}>{rn.busy ? "Застосовую…" : "Змінити"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══ GSC VIEW ═══
